/**
 * TEMU 商品名称：英文标题走 DeepSeek（/listing-export/translate-title）译成中文。
 */

import { listingExportTranslateAPI } from '@/utils/api';
import {
  isLikelyEnglishListingTitle,
  titleContainsCjk,
} from './ensureEnglishListingTitle';

const titleCache = new Map();
const TRANSLATE_MAX_ATTEMPTS = 3;

export function clearChineseListingTitleCache() {
  titleCache.clear();
}

function asTitle(value) {
  return String(value || '').trim();
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientTranslateError(err) {
  const status = Number(err?.status || err?.response?.status || 0);
  const msg = String(err?.message || err?.userMessage || err?.response?.data?.message || '').toLowerCase();
  if (status === 502 || status === 504 || status === 503) return true;
  return /连接被重置|connection reset|超时|timeout|稍后重试|network/.test(msg);
}

/**
 * 已是中文则原样返回；纯英文则译成中文。
 * @param {string} title
 * @param {{ fallbackChinese?: string }} [options]
 *   fallbackChinese：翻译失败时若已有中文标题（展示/库内其它字段），直接用它，避免误杀
 * @returns {Promise<string>}
 */
export async function ensureChineseListingTitle(title, options = {}) {
  const text = asTitle(title);
  if (!text) return '';
  if (titleContainsCjk(text)) return text;
  const fallbackChinese = asTitle(options.fallbackChinese);
  if (!isLikelyEnglishListingTitle(text)) {
    // 货号/混合短串：不强翻，避免把 SKU 译坏；有中文兜底则优先中文
    return titleContainsCjk(fallbackChinese) ? fallbackChinese : text;
  }
  if (titleCache.has(text)) {
    const cached = titleCache.get(text);
    if (cached && titleContainsCjk(cached)) return cached;
  }

  let lastErr = null;
  for (let attempt = 1; attempt <= TRANSLATE_MAX_ATTEMPTS; attempt += 1) {
    try {
      const result = await listingExportTranslateAPI.translate(text, 'zh', 'en');
      const translated = asTitle(result?.translatedText || result?.data?.translatedText);
      if (translated && titleContainsCjk(translated)) {
        titleCache.set(text, translated);
        return translated;
      }
      lastErr = new Error('DeepSeek 译文无效或仍无中文');
    } catch (err) {
      lastErr = err;
      if (!isTransientTranslateError(err) || attempt >= TRANSLATE_MAX_ATTEMPTS) {
        break;
      }
      await sleep(400 * attempt);
    }
  }

  if (titleContainsCjk(fallbackChinese)) {
    return fallbackChinese;
  }

  const preview = text.length > 48 ? `${text.slice(0, 48)}…` : text;
  const detail = String(lastErr?.message || lastErr?.userMessage || '').trim();
  throw new Error(
    detail
      ? `英文标题未能译成中文，TEMU 商品名称需要中文（原文：${preview}；${detail}）`
      : `英文标题未能译成中文，TEMU 商品名称需要中文（原文：${preview}）`,
  );
}
